import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useDepartmentMemberships } from '@/features/access/hooks';
import type { AccessGroup } from '@/shared/access';
import type { NormalizedApiError } from '@/shared/api';
import { usePermissions } from '@/shared/permissions';
import { useSession } from '@/shared/session';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import type { Employee, EmployeeStatus } from '../../employeesApi';
import {
  useDeactivateEmployee,
  useEmployees,
  useReactivateEmployee,
  useResendInvitation,
  useRevokeInvitation,
  useSuspendEmployee,
  useTransferOwnership,
} from '../../hooks';
import { EMPLOYEES_MANAGE } from '../../permissions';
import { ChangeRoleDialog } from '../components/ChangeRoleDialog';
import { InviteEmployeeDialog } from '../components/InviteEmployeeDialog';
import { UserGroupsDrawer } from '../components/UserGroupsDrawer';

/** Long enough to swallow ordinary typing, short enough that the table does not feel stuck. */
const SEARCH_DEBOUNCE_MS = 350;

const STATUS_COLOR: Record<EmployeeStatus, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  DISABLED: 'default',
};

/**
 * The company's staff list.
 *
 * <p>Every destructive action is confirmed and every guarded one explains itself rather than appearing
 * as a dead control: an administrator cannot act on their own row, and the last owner cannot be suspended
 * or removed. Those rules are enforced by the BFF — this screen shows them so the refusal is not a
 * surprise arriving as a 403.
 *
 * <p>The screen has two shapes, because the platform now has two staff permissions (plan-auth-001 Phase
 * 3.1/3.3). With `employees.manage` it is the administration screen described above. With only
 * `employees.view` it is a **directory**: the same table, and no mutating affordance anywhere — no Invite
 * button, no row menu, not even an Actions column. Absent, not disabled: a disabled control is a promise
 * that the right permission would light it up, and an operations manager reading the staff list is not
 * one click away from suspending someone. The info alert says what state they are in instead of leaving
 * them to infer it from a table full of greyed-out buttons.
 */
/**
 * A person's departments in one table cell, and the way into the panel that edits them.
 *
 * <p>Two chips at most. A cell that grows with membership makes the whole table reflow around whoever
 * happens to be in the most departments, and the third name is never the one that answers the question —
 * "+2" plus a click is more useful than three truncated words.
 *
 * <p>Someone in no department at all is called out rather than left blank: it is the state that silently
 * means "signed in and can do nothing", and it is usually an onboarding that was never finished.
 */
function DepartmentsCell({
  groups,
  loading,
  onOpen,
  label,
  emptyLabel,
  moreLabel,
}: {
  groups: AccessGroup[];
  loading: boolean;
  onOpen: () => void;
  label: string;
  emptyLabel: string;
  moreLabel: (count: number) => string;
}) {
  if (loading) {
    return <Skeleton variant="rounded" width={120} height={24} />;
  }
  const shown = groups.slice(0, 2);
  const rest = groups.length - shown.length;

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={label}
      sx={{
        // A button, so it is reachable by keyboard and announced as the action it is — styled flat so
        // the table still reads as a table.
        border: 0,
        p: 0.5,
        m: -0.5,
        bgcolor: 'transparent',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {groups.length === 0 ? (
        <Chip size="small" variant="outlined" color="warning" label={emptyLabel} />
      ) : (
        <>
          {shown.map((group) => (
            <Chip key={group.id} size="small" variant="outlined" label={group.name} />
          ))}
          {rest > 0 && <Chip size="small" label={moreLabel(rest)} />}
        </>
      )}
      <GroupsRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
    </Stack>
  );
}

export function UsersPage() {
  const { t } = useTranslation('employees');
  const session = useSession();
  const { hasPermission } = usePermissions();
  const callerId = session.user?.id;
  const canManage = hasPermission(EMPLOYEES_MANAGE);

  const [search, setSearch] = useState('');
  // What actually reaches the query key. Typing must not become one request per keystroke: each list call
  // costs a Keycloak members lookup PLUS a role lookup per row, so an un-debounced 6-character search was
  // ~150 admin calls. Mirrors the pattern in `ProductAutocomplete`.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<EmployeeStatus | ''>('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [menuFor, setMenuFor] = useState<{ el: HTMLElement; employee: Employee } | null>(null);
  const [confirm, setConfirm] = useState<{ employee: Employee; action: ConfirmAction } | null>(null);
  const [roleFor, setRoleFor] = useState<Employee | null>(null);
  /** The person whose departments the right-hand panel is showing. */
  const [groupsFor, setGroupsFor] = useState<Employee | null>(null);
  /** Surfaced in the confirmation dialog, and above the table for the actions that have no dialog. */
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useMemo(
    () => ({ search: debouncedSearch.trim() || undefined, status: status || undefined, page, size }),
    [debouncedSearch, status, page, size],
  );
  const employees = useEmployees(query);

  const suspend = useSuspendEmployee();
  const reactivate = useReactivateEmployee();
  const remove = useDeactivateEmployee();
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const transfer = useTransferOwnership();

  const items = employees.data?.items ?? [];
  const total = employees.data?.total ?? -1;

  /*
   * Departments per person, for the column and the panel alike.
   *
   * Only for administrators: every access endpoint behind this is `employees.manage` on the BFF, so a
   * view-only reader would earn a column of 403s. They get the directory without it rather than a column
   * that cannot fill.
   *
   * One request per department, shared by every row and cached — see `useDepartmentMemberships` for why
   * the platform leaves the inversion to the client.
   */
  const memberships = useDepartmentMemberships({ enabled: canManage });

  /*
   * Can this page actually see every employee?
   *
   * `ownerCount` used to be counted straight off `items`, which is one page. On page 2 — or under any
   * status filter — it came out 0, the last-owner guard silently switched itself off, and the menu happily
   * offered "Suspend" on the sole owner. The BFF still refused, so the result was the 400-instead-of-an-
   * explanation this screen exists to prevent.
   *
   * A filtered or paged view cannot count owners, so it does not pretend to: `ownerCountIsKnown` is false
   * and every OWNER is then treated as potentially the last one. Erring that way costs an administrator one
   * extra click through "transfer ownership"; erring the other way offers an action that cannot succeed.
   *
   * A SEARCH is a filter (GAP F-9d). The condition used to name only the status filter, contradicting the
   * comment above it: searching "anna" on a two-owner company returns one owner, `items.length >= total`
   * holds for that result set, and the guard concluded there was exactly one owner in the company. Safe in
   * its error direction — it over-restricts — but the code has to mean what it says.
   */
  const ownerCountIsKnown = !status && !debouncedSearch.trim() && total >= 0 && items.length >= total;
  const ownerCount = items.filter((e) => e.role === 'OWNER').length;

  const closeMenu = () => setMenuFor(null);

  /*
   * Every action used to pass `onSettled: done`, which closes the dialog whether the call succeeded or
   * failed. A refusal — the last owner, your own row, a person who works for two companies — therefore
   * produced a dialog that simply closed, and nothing happened. On a screen whose whole job is to make
   * refusals legible, that was the worst possible outcome.
   *
   * Now the dialog closes only on success; a failure keeps it open and shows why.
   */
  const runConfirmed = () => {
    if (!confirm) return;
    const id = confirm.employee.id;
    const handlers = {
      onSuccess: () => {
        setActionError(null);
        setConfirm(null);
      },
      onError: (error: NormalizedApiError) =>
        setActionError(error.message || t('actionFailed')),
    };
    setActionError(null);
    switch (confirm.action) {
      case 'suspend':
        suspend.mutate(id, handlers);
        break;
      case 'remove':
        remove.mutate(id, handlers);
        break;
      case 'revoke':
        revoke.mutate(id, handlers);
        break;
      case 'transfer':
        transfer.mutate(id, handlers);
        break;
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label={t('filters.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label={t('filters.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as EmployeeStatus | '');
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('filters.anyStatus')}</MenuItem>
          <MenuItem value="ACTIVE">{t('statuses.active')}</MenuItem>
          <MenuItem value="INVITED">{t('statuses.invited')}</MenuItem>
          <MenuItem value="DISABLED">{t('statuses.disabled')}</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        {canManage && (
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1RoundedIcon />}
            onClick={() => setInviteOpen(true)}
          >
            {t('inviteButton')}
          </Button>
        )}
      </Stack>

      {!canManage && <Alert severity="info">{t('readOnly.notice')}</Alert>}

      {actionError && confirm === null && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {employees.isLoading ? (
        <LoadingState label={t('loading')} />
      ) : employees.isError ? (
        <ErrorState
          title={t('loadError.title')}
          message={employees.error?.message ?? ''}
          onRetry={() => void employees.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('empty.title')}
          // A view-only reader cannot fix an empty staff list, so they are not told to invite someone.
          description={canManage ? t('empty.description') : t('empty.readOnlyDescription')}
          action={
            canManage ? (
              <Button variant="contained" onClick={() => setInviteOpen(true)}>
                {t('inviteButton')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('table.employee')}</TableCell>
                <TableCell>{t('table.role')}</TableCell>
                <TableCell>{t('table.status')}</TableCell>
                {/* Departments are what actually confer access, so they belong beside the role rather
                    than a screen away. Administrators only: the data behind them needs `employees.manage`. */}
                {canManage && <TableCell>{t('table.departments')}</TableCell>}
                {/* No actions to offer, so no column: an always-empty column is noise, not a promise. */}
                {canManage && <TableCell align="right">{t('table.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((employee) => {
                const isSelf = employee.id === callerId;
                const isLastOwner =
                  employee.role === 'OWNER' && (!ownerCountIsKnown || ownerCount <= 1);
                return (
                  <TableRow key={employee.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {employee.name ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {employee.role ? (
                        <Chip
                          size="small"
                          color={employee.role === 'OWNER' ? 'primary' : 'default'}
                          label={t(`roles.${employee.role.toLowerCase()}`)}
                        />
                      ) : (
                        // Not a missing value to paper over: this person is in the company and holds no
                        // portal role, so they can sign in and do nothing. Worth seeing at a glance —
                        // it is usually someone onboarded and then forgotten.
                        <Tooltip title={t('roles.noneHint')}>
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={t('roles.none')}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={STATUS_COLOR[employee.status] ?? 'default'}
                        label={t(`statuses.${String(employee.status).toLowerCase()}`, employee.status)}
                      />
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <DepartmentsCell
                          groups={memberships.byUser.get(employee.id) ?? []}
                          loading={memberships.isLoading}
                          onOpen={() => setGroupsFor(employee)}
                          label={t('groups.openAria', { email: employee.email })}
                          emptyLabel={t('groups.none')}
                          moreLabel={(count: number) => t('groups.more', { count })}
                        />
                      </TableCell>
                    )}
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip
                          title={
                            isSelf
                              ? t('guards.self')
                              : isLastOwner
                                ? t('guards.lastOwner')
                                : t('table.actions')
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={isSelf}
                              aria-label={t('rowActionsAria', { email: employee.email })}
                              onClick={(e) => setMenuFor({ el: e.currentTarget, employee })}
                            >
                              <MoreVertRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            // The identity provider cannot always supply a count; -1 makes MUI show "more than" paging
            // instead of a wrong total.
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      {canManage && (
        <>
          <RowMenu
            anchor={menuFor}
            ownerCount={ownerCount}
            ownerCountIsKnown={ownerCountIsKnown}
            onClose={closeMenu}
            onAction={(employee, action) => {
              closeMenu();
              setConfirm({ employee, action });
            }}
            onChangeRole={(employee) => {
              closeMenu();
              setActionError(null);
              setRoleFor(employee);
            }}
            onResend={(employee) => {
              closeMenu();
              setActionError(null);
              resend.mutate(employee.id, {
                onError: (error) => setActionError(error.message || t('actionFailed')),
              });
            }}
            onReactivate={(employee) => {
              closeMenu();
              setActionError(null);
              reactivate.mutate(employee.id, {
                onError: (error) => setActionError(error.message || t('actionFailed')),
              });
            }}
          />

          <InviteEmployeeDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

          <UserGroupsDrawer employee={groupsFor} onClose={() => setGroupsFor(null)} />

          <ChangeRoleDialog employee={roleFor} onClose={() => setRoleFor(null)} />

          <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
            <DialogTitle>{confirm ? t(`confirm.${confirm.action}.title`) : ''}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirm ? t(`confirm.${confirm.action}.body`, { email: confirm.employee.email }) : ''}
              </DialogContentText>
              {actionError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {actionError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setActionError(null);
                  setConfirm(null);
                }}
              >
                {t('common:action.cancel')}
              </Button>
              <Button color="error" variant="contained" onClick={runConfirmed}>
                {confirm ? t(`confirm.${confirm.action}.action`) : ''}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Stack>
  );
}

type ConfirmAction = 'suspend' | 'remove' | 'revoke' | 'transfer';

interface RowMenuProps {
  anchor: { el: HTMLElement; employee: Employee } | null;
  ownerCount: number;
  /** False when the list is paged, searched or filtered, so owners cannot be counted — see UsersPage. */
  ownerCountIsKnown: boolean;
  onClose: () => void;
  onAction: (employee: Employee, action: ConfirmAction) => void;
  onChangeRole: (employee: Employee) => void;
  onResend: (employee: Employee) => void;
  onReactivate: (employee: Employee) => void;
}

/** The per-row action menu. Options absent here are ones the BFF would refuse anyway. */
function RowMenu({
  anchor,
  ownerCount,
  ownerCountIsKnown,
  onClose,
  onAction,
  onChangeRole,
  onResend,
  onReactivate,
}: RowMenuProps) {
  const { t } = useTranslation('employees');
  if (!anchor) return null;

  const { employee } = anchor;
  const isLastOwner = employee.role === 'OWNER' && (!ownerCountIsKnown || ownerCount <= 1);
  const isPending = employee.status === 'INVITED';

  return (
    <Menu anchorEl={anchor.el} open onClose={onClose}>
      {isPending && (
        <MenuItem onClick={() => onResend(employee)}>
          <ListItemText primary={t('actions.resendInvitation')} />
        </MenuItem>
      )}
      {isPending && (
        <MenuItem onClick={() => onAction(employee, 'revoke')}>
          <ListItemText primary={t('actions.revokeInvitation')} />
        </MenuItem>
      )}
      {/*
        Changing a role is the ordinary correction — someone was invited as STAFF and should be a
        MANAGER — so it sits at the top of the non-invitation actions. Demoting the last OWNER is
        refused by the BFF, so it is disabled here with the same explanation the destructive actions use.
      */}
      <MenuItem disabled={isLastOwner} onClick={() => onChangeRole(employee)}>
        <ListItemText
          primary={t('actions.changeRole')}
          secondary={isLastOwner ? t('guards.lastOwner') : t('actions.changeRoleHint')}
        />
      </MenuItem>
      {/*
        Transfer is offered only for an ACTIVE non-owner (GAP F-9c). It used to appear on every non-OWNER
        row, which meant a company could be handed to an INVITED account that has never signed in, or to a
        DISABLED one that cannot — a foot-gun on a screen built to prevent exactly those.
      */}
      {employee.role !== 'OWNER' && employee.status === 'ACTIVE' && (
        <MenuItem onClick={() => onAction(employee, 'transfer')}>
          <ListItemText
            primary={t('actions.transferOwnership')}
            secondary={t('actions.transferOwnershipHint')}
          />
        </MenuItem>
      )}
      {employee.status === 'DISABLED' && (
        <MenuItem onClick={() => onReactivate(employee)}>
          <ListItemText
            primary={t('actions.reactivate')}
            secondary={t('actions.reactivateHint')}
          />
        </MenuItem>
      )}
      {employee.status !== 'DISABLED' && (
        <MenuItem disabled={isLastOwner} onClick={() => onAction(employee, 'suspend')}>
          <ListItemText
            primary={t('actions.suspend')}
            secondary={isLastOwner ? t('guards.lastOwner') : t('actions.suspendHint')}
          />
        </MenuItem>
      )}
      <MenuItem disabled={isLastOwner} onClick={() => onAction(employee, 'remove')}>
        <ListItemText
          primary={t('actions.remove')}
          secondary={isLastOwner ? t('guards.lastOwner') : undefined}
        />
      </MenuItem>
    </Menu>
  );
}
